/* ============================================================
   EDITOR FORMULA  (/formula/editor)  — SESAAT Studio v3.9
   ============================================================
   Dibuka daripada butang "Start building" pada rak Formulas.

   ⭐⭐ FORMULA = MASTER PROMPT PELANGGAN (pembetulan user 2026-08-09).
      Inti formula ialah master prompt yang pelanggan tulis sendiri —
      pipeline mengambil prompt itu untuk menjana full prompt (gambar +
      video + caption) bagi setiap produk. Dropdown gaya BUKAN formula;
      ia hanya rasa formula.

   ⭐ DUA MOD PROMPT (keputusan user 2026-08-09):
        single   — satu master prompt untuk semua set
        separate — satu master prompt PENUH setiap set (tab Set 1/2/3)
      TIADA kotak per medan (gambar/video/caption berasingan) — user
      tolak struktur itu; kita bagi kotak teks sahaja.

   ⭐⭐ KONTRAK OUTPUT MILIK SISTEM, BUKAN PELANGGAN.
      Server hari ini berkata "Follow the user instruction's output format
      EXACTLY as specified" — bermakna nama medan JSON ditulis DALAM teks
      master prompt. Kalau pelanggan tulis prompt sendiri tanpa bahagian
      itu: AI pulangkan bentuk lain → Generate nampak berjaya → kad lahir
      KOSONG → Run Flow tidak jumpa prompt gambar lalu jatuh kepada NAMA
      PRODUK → video sampah, 20 minit terbuang, tanpa satu pun error.
      Sebab itu kontrak dicop sistem dan dipapar read-only di langkah 3.

   ⭐ SUSUN ATUR "B" — rel langkah BERNAMA di kiri, satu lajur soalan,
      bar butang merentas kaki halaman. Ukuran hidup dalam kelas .s-fs__*
      (design/ui.css) — jangan tulis nilai baharu di sini.

   ⛔ LIMA MASALAH SUSUN ATUR YANG SUDAH DIBETULKAN — jangan pulangkan:
        1. Nama formula ada DUA medan (bar tajuk + langkah 1). Sekarang
           bar tajuk hanya MEMAPAR nama.
        2. Soalan 33% lawan pratonton 66% — keutamaan terbalik.
        3. Butang sticky pada kaki LAJUR → terapung di tengah skrin.
           Sekarang bar kaki HALAMAN.
        4. Tiga perenggan + kotak KUNING untuk maklumat biasa. Kuning
           kini untuk amaran benar sahaja.
        5. Lebih separuh skrin kosong — akibat 2, 3, 4 serentak.

   ⛔ RANGKA CONTOH DI BAWAH DITULIS KHAS UNTUK PELANGGAN. Ia BUKAN
      salinan master prompt AI8SAAT/GOYANG/AI16SAAT — itu resepi produk,
      disimpan dalam peti besi admin-sahaja.

   ⚠️ PROTOTAIP — tiada Firestore, tiada panggilan AI. "Uji formula"
      memapar keputusan yang ditulis dalam fail ini (termasuk suis untuk
      melihat keadaan GAGAL), dan skrin itu mengaku begitu. Simpan hanya
      menavigasi ke /workspace-v3.9.

   Reka bentuk penuh: PLAN_FORMULA_SENDIRI.md
   ============================================================ */

import { useState } from 'react';
import { ArrowLeft, ArrowRight, Check, Play, Lock, ChevronRight, Sparkles, X, Info } from 'lucide-react';

import '../design/tokens.css';
import { Badge, Btn, PageHeader, Segmented, Card, CardBody } from '../design/ui';
import { useT } from '../i18n';

/* ⚠️ Pemalar aras-modul tidak boleh memanggil useT() — hook hanya sah di
   dalam komponen. Jadi senarai di bawah menyimpan KUNCI kamus sahaja, dan
   ayat sebenar dicari semasa render. */
const LANGKAH = [
    { id: 1, kunciTajuk: 'formula.editor.langkah.nama' },
    { id: 2, kunciTajuk: 'formula.editor.langkah.bentuk' },
    { id: 3, kunciTajuk: 'formula.editor.langkah.prompt' },
    { id: 4, kunciTajuk: 'formula.editor.langkah.uji' },
    { id: 5, kunciTajuk: 'formula.editor.langkah.simpan' },
];
const AKHIR = LANGKAH.length;

/* Enjin yang WUJUD dan sudah diuji. Senarai ini bukan pilihan reka
   bentuk — ia cerminan skrip automation sebenar dalam extension. */
const ENJIN = [
    { id: '1', kunciLabel: 'formula.editor.enjin.satu', kunciNota: 'formula.editor.enjin.satuNota' },
    { id: '2', kunciLabel: 'formula.editor.enjin.dua', kunciNota: 'formula.editor.enjin.duaNota' },
];

/* Dipapar tetapi mati. Robot banyak-babak ada fasa download tersendiri
   dan babak terakhirnya mesti gambar produk sebenar — ia paling mudah
   tersangkut, jadi ia dibuka selepas 1 & 2 babak stabil. */
const ENJIN_BELUM = {
    kunciLabel: 'formula.editor.enjin.banyak',
    kunciNota: 'formula.editor.enjin.banyakNota',
};

/* Rangka permulaan untuk pelanggan yang tidak tahu nak mula dari mana.
   Struktur betul, kualiti biasa — titik mula untuk diedit, bukan resepi.
   Sengaja TIADA bahagian output format: itu kerja sistem. */
const RANGKA_CONTOH = `PERANAN
Anda pengarah kreatif video TikTok untuk produk affiliate.

WATAK
Seorang wanita Melayu awal 20-an, bertudung, pakaian sopan, gaya santai
dan yakin bercakap dengan kamera seperti bercakap dengan kawan.

LOKASI & GAYA
Bilik yang cerah dan bersih. Kamera aras mata, cahaya lembut, rasa
handheld sedikit — bukan gaya iklan studio.

SUDUT CERITA
Buka dengan masalah yang pelanggan rasa setiap hari, kemudian tunjuk
perubahan selepas guna produk.

HOOK
Ayat pertama mesti pendek, bawah lapan patah perkataan, bahasa harian.
Jangan mula dengan nama produk.

CAPTION
Santai. Satu ayat manfaat, satu ajakan. Tambah tiga hashtag berkaitan.

PANTANG LARANG
Jangan reka fakta produk — guna data produk yang diberi sahaja.
Jangan janji hasil perubatan.`;

/* Kontrak output yang server cop SELEPAS prompt pelanggan. Dibina
   daripada bentuk video + bilangan set + mod, supaya pelanggan nampak
   kontrak SEBENAR untuk pilihannya — bukan contoh am. */
function binaKontrak(enjin, bilSet, mod) {
    const sets = Array.from({ length: bilSet }, (_, i) => `set${i + 1}`).join(' · ');
    const medan = enjin === '2'
        ? 'promptImage · promptVideoScene1 · promptVideoScene2 · captionTiktok'
        : 'promptImage · promptVideo · captionTiktok';
    const barisVideo = enjin === '2'
        ? 'promptVideoScene1/2  visual English + dialog Bahasa Melayu'
        : 'promptVideo           visual English + dialog Bahasa Melayu';
    const ikatan = mod === 'separate'
        ? '\n\nIKATAN SEKSYEN (mod setiap set)\n'
          + Array.from({ length: bilSet }, (_, i) =>
              `set${i + 1} mesti ikut ARAHAN SET ${i + 1} sahaja — jangan campur gaya set lain.`).join('\n')
        : '';
    return `OUTPUT FORMAT (WAJIB — sistem yang tambah)

Pulangkan JSON sahaja. Tiada teks lain, tiada code block.
Kunci peringkat atas: ${sets}
Setiap set mengandungi: ${medan}

promptImage           tulis SEPENUHNYA dalam English
${barisVideo}
captionTiktok         Bahasa Melayu santai${ikatan}

Data produk sebenar (nama, harga, spesifikasi, ulasan, avatar, gender)
dilampirkan automatik di hujung prompt — anda tidak perlu menulisnya.`;
}

/* Teks contoh untuk skrin hasil ujian. Prompt gambar dan video sengaja
   dalam bahasa Inggeris, caption dalam BM — itu konvensyen output
   sebenar sistem, jadi prototaip pun mengajar perkara yang betul. */
const CONTOH_HASIL = {
    gambar: 'A young Malaysian woman in a soft beige hijab holding a serum bottle at chest level, '
        + 'modern bright bathroom, eye level, soft window light, text overlay: "Kulit kusam 3 minggu je?"',
    video: 'She lifts the serum bottle toward the camera and smiles, slow push-in, 8 seconds, '
        + 'natural handheld feel.\n\nSpoken dialog (exact script):\n'
        + '"Tiga minggu guna ni, muka aku terang balik."',
    video2: 'Close-up of her hands applying two drops onto the back of her hand, then a wider shot '
        + 'of her smiling at the mirror.\n\nSpoken dialog (exact script):\n'
        + '"Dua titik je cukup — pagi dan malam."',
    caption: 'Kulit kusam? Tiga minggu je nampak beza. Link di bio.\n#skincare #serum #kulitglowing',
};

/* ---------- Bahagian kecil ------------------------------------------ */

function Soalan({ tajuk, nota, lencana, children }) {
    return (
        <section>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <h2 className="s-fs__soalan">{tajuk}</h2>
                {lencana}
            </div>
            {nota && <p className="s-fs__nota">{nota}</p>}
            {children}
        </section>
    );
}

function Medan({ label, children }) {
    return (
        <div style={{ minWidth: 0 }}>
            <div className="s-fe__lbl">{label}</div>
            {children}
        </div>
    );
}

function KotakHasil({ label, teks }) {
    return (
        <div className="s-fs__box">
            <div className="s-fe__lbl" style={{ marginBottom: 5 }}>{label}</div>
            <p className="s-fs__boxteks">{teks}</p>
        </div>
    );
}

function BarisRingkasan({ label, children }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'baseline', gap: 12,
            padding: '9px 0', borderBottom: '1px solid var(--s-line)',
        }}>
            <span className="s-fe__lbl" style={{ width: 148, flex: 'none', marginBottom: 0 }}>{label}</span>
            <span style={{ fontSize: 13, color: 'var(--s-ink)', minWidth: 0 }}>{children}</span>
        </div>
    );
}

/* ---------- Halaman -------------------------------------------------- */

export default function FormulaEditorV39View({ onNavigate }) {
    /* Halaman v3.9 sahaja — tiada rupa lama, jadi useT() boleh dipanggil
       terus tanpa pembalut ayat lalai. */
    const t = useT();


    const [langkah, setLangkah] = useState(1);

    const [nama, setNama] = useState('');
    const [huraian, setHuraian] = useState('');
    const [enjin, setEnjin] = useState('');          // '' = belum pilih (langkah 2 menghalang)
    const [bilSet, setBilSet] = useState(3);

    const [promptMode, setPromptMode] = useState('single');
    const [prompt, setPrompt] = useState('');                                   // mod single
    const [promptSets, setPromptSets] = useState({ set1: '', set2: '', set3: '' }); // mod separate
    const [setAktif, setSetAktif] = useState(1);
    const [bukaKontrak, setBukaKontrak] = useState(false);

    /* Siapa yang dapat formula ini (arahan user 2026-08-12).
         sendiri — hanya admin yang membinanya
         system  — SEMUA pelanggan berlesen nampak formula ini

       Lalai `sendiri`, dan itu disengajakan: keadaan lalai bagi butang yang
       kesannya paling luas mestilah keadaan yang paling kecil kesannya.
       Mengedar kepada semua pelanggan hendaklah satu tindakan yang SEDAR.

       ⛔ Kawalan ini ADMIN SAHAJA. Halaman ini prototaip (tiada Firestore,
          tiada isAdmin), jadi di sini ia sentiasa dipapar supaya reka
          bentuknya boleh dinilai. Bila editor ini disambungkan kepada data
          sebenar: pagarkan dengan isAdmin, dan simpan nilainya pada doc
          module — pelanggan biasa langsung tidak boleh melihat kawalan ini,
          apatah lagi menekannya. */
    const [milik, setMilik] = useState('sendiri');

    /* 'belum' → 'jalan' → 'lulus' | 'gagal'. Prototaip: tiada panggilan AI. */
    const [ujian, setUjian] = useState('belum');
    /* Suis PROTOTAIP sahaja — supaya keadaan GAGAL boleh dilihat dan dinilai.
       Dalam halaman sebenar, keputusan datang daripada output AI. */
    const [tunjukGagal, setTunjukGagal] = useState(false);

    const duaBabak = enjin === '2';
    const senaraiSet = Array.from({ length: bilSet }, (_, i) => i + 1);
    const kunci = (n) => `set${n}`;

    const teksAktif = promptMode === 'single' ? prompt : (promptSets[kunci(setAktif)] || '');
    const setKosong = senaraiSet.filter((n) => !String(promptSets[kunci(n)] || '').trim());
    const promptSiap = promptMode === 'single' ? !!prompt.trim() : setKosong.length === 0;

    /* Set yang "gagal" dalam demo = set KEDUA kalau ada, kalau tidak set 1.
       Prototaip sahaja. */
    const setGagal = senaraiSet.length > 1 ? senaraiSet[1] : senaraiSet[0];

    const bolehTeruskan = (() => {
        if (langkah === 1) return nama.trim().length > 0;
        if (langkah === 2) return !!enjin;
        if (langkah === 3) return promptSiap;
        if (langkah === 4) return ujian === 'lulus';
        return true;
    })();

    const petunjukSekat = (() => {
        if (bolehTeruskan) return '';
        if (langkah === 1) return t('formula.editor.sekat.nama');
        if (langkah === 2) return t('formula.editor.sekat.bentuk');
        if (langkah === 3) {
            if (promptMode === 'single') return t('formula.editor.sekat.prompt');
            return t('formula.editor.sekat.setKosong', { senarai: setKosong.join(', ') });
        }
        if (langkah === 4) {
            return ujian === 'gagal'
                ? t('formula.editor.sekat.ujiGagal')
                : t('formula.editor.sekat.ujiBelum');
        }
        return '';
    })();

    const kebelakang = () => setLangkah((n) => Math.max(1, n - 1));
    const kehadapan = () => {
        if (!bolehTeruskan) return;
        if (langkah === AKHIR) {
            /* Halaman sebenar akan menulis formula dahulu (termasuk master
               prompt), kemudian menetapkannya sebagai formula aktif supaya
               Generate seterusnya di Workspace memang menggunakannya. */
            onNavigate?.('/workspace-v3.9');
            return;
        }
        setLangkah((n) => n + 1);
    };

    /* Edit prompt selepas ujian membatalkan keputusan itu — kalau tidak,
       pelanggan boleh lulus dengan satu prompt kemudian menyimpan prompt
       yang lain sama sekali. */
    const tukarTeks = (nilai) => {
        if (promptMode === 'single') setPrompt(nilai);
        else setPromptSets((v) => ({ ...v, [kunci(setAktif)]: nilai }));
        if (ujian !== 'belum') setUjian('belum');
    };

    /* Bertukar mod TIDAK boleh menghilangkan kerja yang sudah ditaip —
       itu cara terpantas hilang kepercayaan pelanggan. Teks dibawa masuk
       ke laluan baharu kalau tempat destinasi masih kosong. */
    const tukarMod = (m) => {
        if (m === promptMode) return;
        if (m === 'separate' && prompt.trim() && !String(promptSets.set1 || '').trim()) {
            setPromptSets((v) => ({ ...v, set1: prompt }));
        }
        if (m === 'single' && !prompt.trim() && String(promptSets.set1 || '').trim()) {
            setPrompt(promptSets.set1);
        }
        setPromptMode(m);
        setSetAktif(1);
        if (ujian !== 'belum') setUjian('belum');
    };

    const jalankanUjian = () => {
        setUjian('jalan');
        setTimeout(() => setUjian(tunjukGagal ? 'gagal' : 'lulus'), 900);
    };

    const betulkanSet = (n) => {
        setSetAktif(n);
        setUjian('belum');
        setLangkah(3);
    };

    const jumlahAksara = promptMode === 'single'
        ? prompt.trim().length
        : senaraiSet.reduce((j, n) => j + String(promptSets[kunci(n)] || '').trim().length, 0);

    return (
        <div className="s-work">

            {/* Bar 56px yang sama seperti Workspace. Nama di sini hanya
                DIPAPAR — medan sebenar milik langkah 1. */}
            <div className="s-work__head">
                <PageHeader
                    className="s-work__pagehead"
                    title={
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                            <button
                                type="button"
                                onClick={() => onNavigate?.('/formula')}
                                aria-label={t('formula.editor.tajuk.kembali')}
                                title={t('formula.editor.tajuk.kembali')}
                                className="s-fe__back"
                            >
                                <ArrowLeft size={15} strokeWidth={1.9} />
                            </button>
                            <span style={{
                                minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                color: nama.trim() ? 'var(--s-ink)' : 'var(--s-ink-3)',
                            }}>
                                {nama.trim() || t('formula.editor.tajuk.tanpaNama')}
                            </span>
                            <span className="s-dim" style={{ flex: 'none' }}>{t('formula.editor.tajuk.draf')}</span>
                        </span>
                    }
                    actions={
                        <>
                            {/* Siapa dapat formula ini — di bar tajuk, kelihatan
                                pada SEMUA langkah (arahan user 2026-08-12).
                                Sebabnya ia mengubah cara formula ditulis: yang
                                untuk system mesti cukup umum untuk sebarang
                                produk, yang milik sendiri boleh sesempit mana
                                pun. Menyorokkannya dalam satu langkah bermakna
                                admin menulis tiga langkah tanpa nampak untuk
                                siapa dia menulis. */}
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flex: 'none' }}>
                                {/* Lencana senyap, bukan pil ungu — ia label
                                    "siapa nampak kawalan ni", bukan kawalan. */}
                                <span className="s-dim" style={{ flex: 'none', fontSize: 11 }}>{t('formula.editor.milik.label')}</span>
                                <Segmented
                                    value={milik}
                                    onChange={setMilik}
                                    options={[
                                        { value: 'sendiri', label: t('formula.editor.milik.sendiri') },
                                        { value: 'system', label: t('formula.editor.milik.system') },
                                    ]}
                                />
                            </span>

                            {/* ⛔ Ayat akibat DIKELUARKAN dari baris ini (aduan
                                user 2026-08-12: "berterabur"). Ia ayat penuh
                                sepanjang 45 aksara di dalam bar kawalan setinggi
                                56px, berhimpit dengan lencana, pemilih, label
                                prototaip dan butang simpan — lima benda berebut
                                satu baris.

                                Ia tidak dibuang, cuma berpindah ke jalur di
                                bawah bar (lihat di bawah) dan hanya muncul bila
                                ia BERMAKNA, iaitu bila "Untuk system" dipilih. */}
                            {/* "Simpan draf" BERPINDAH ke bar kaki (aduan user
                                2026-08-12: bar tajuk semak). Ia tindakan ke atas
                                seluruh wizard, sama keluarga dengan Kembali dan
                                Seterusnya — dan bar kaki itu memang sudah jadi
                                tempat tindakan. Meletakkannya di bar tajuk
                                bermakna tindakan wizard bertaburan di DUA
                                hujung skrin. */}
                            <span className="s-dim">{t('formula.editor.prototaip')}</span>
                        </>
                    }
                />
            </div>

            {/* Jalur akibat — muncul HANYA bila formula ini akan diedarkan.
                Keadaan "Milik saya" tidak perlu diumumkan: tiada apa berlaku
                kepada sesiapa, dan sebaris teks yang sentiasa ada akan
                berhenti dibaca dalam masa dua hari.

                Ia jalur nipis merentas lebar, bukan pil dalam bar tajuk —
                di sinilah tempatnya ayat penuh boleh bernafas. */}
            {milik === 'system' && (
                <div style={{
                    flex: 'none', display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 28px',
                    borderBottom: '1px solid var(--s-line)',
                    background: 'var(--s-fill)',
                    fontSize: 12.5, color: 'var(--s-ink-2)',
                }}>
                    <Info size={14} strokeWidth={1.9} style={{ flex: 'none', color: 'var(--s-ink-3)' }} />
                    {/* ⚠️ <b> yang dulu membalut "semua pelanggan berlesen" DIBUANG.
                        Kamus menyimpan ayat ini sebagai SATU kunci penuh, dan
                        memecahkannya kepada tiga serpihan (sebelum/tebal/selepas)
                        akan pecah dalam English — susunan perkataannya berbeza,
                        jadi serpihan tengah tidak lagi jatuh di tempat yang sama.
                        Ayat penuh + ikon Info sudah cukup menandakan jalur ini. */}
                    {t('formula.editor.edar.notis')}
                </div>
            )}

            <div className="s-fs__body">

                {/* ---------- Rel langkah bernama ---------- */}
                <nav className="s-fs__rail" aria-label={t('formula.editor.rel.aria')}>
                    {LANGKAH.map((l) => {
                        const lalu = l.id < langkah;
                        const kini = l.id === langkah;
                        const tajukLangkah = t(l.kunciTajuk);
                        return (
                            <button
                                key={l.id}
                                type="button"
                                disabled={!lalu}
                                onClick={() => lalu && setLangkah(l.id)}
                                aria-current={kini ? 'step' : undefined}
                                title={lalu ? t('formula.editor.rel.kembaliKe', { tajuk: tajukLangkah }) : tajukLangkah}
                                className={
                                    's-fs__step'
                                    + (kini ? ' s-fs__step--on' : '')
                                    + (lalu ? ' s-fs__step--done' : '')
                                }
                            >
                                <span className="s-fs__no">{l.id}</span>
                                <span style={{ flex: 1, minWidth: 0 }}>{tajukLangkah}</span>
                                {lalu && <Check size={13} strokeWidth={2.2} style={{ flex: 'none', color: 'var(--s-ink-3)' }} />}
                            </button>
                        );
                    })}
                </nav>

                {/* ---------- Satu lajur soalan ---------- */}
                <div className="s-fs__main">
                    <div className="s-fs__kolum">

                        {/* ---------- 1 · Nama ---------- */}
                        {langkah === 1 && (
                            <Soalan
                                tajuk={t('formula.editor.s1.tajuk')}
                                nota={t('formula.editor.s1.nota')}
                            >
                                <div style={{ display: 'grid', gap: 9 }}>
                                    <input
                                        className="s-fe__inp"
                                        value={nama}
                                        onChange={(e) => setNama(e.target.value)}
                                        placeholder={t('formula.editor.s1.namaPlaceholder')}
                                        aria-label={t('formula.editor.s1.namaAria')}
                                    />
                                    <textarea
                                        className="s-fe__inp"
                                        style={{ minHeight: 68, resize: 'vertical' }}
                                        value={huraian}
                                        onChange={(e) => setHuraian(e.target.value)}
                                        placeholder={t('formula.editor.s1.huraianPlaceholder')}
                                        aria-label={t('formula.editor.s1.huraianAria')}
                                    />
                                </div>
                            </Soalan>
                        )}

                        {/* ---------- 2 · Bentuk video ---------- */}
                        {langkah === 2 && (
                            <Soalan
                                tajuk={t('formula.editor.s2.tajuk')}
                                nota={t('formula.editor.s2.nota')}
                            >
                                <div style={{ display: 'grid', gap: 18 }}>
                                    <div style={{ display: 'grid', gap: 7 }}>
                                        {ENJIN.map((e) => (
                                            <button
                                                key={e.id}
                                                type="button"
                                                aria-pressed={enjin === e.id}
                                                onClick={() => setEnjin(e.id)}
                                                className={enjin === e.id ? 's-fe__opt s-fe__opt--on' : 's-fe__opt'}
                                            >
                                                <span style={{ flex: 1, minWidth: 0 }}>
                                                    <span style={{ display: 'block', fontSize: 13, color: 'var(--s-ink)' }}>{t(e.kunciLabel)}</span>
                                                    <span className="s-dim">{t(e.kunciNota)}</span>
                                                </span>
                                                {enjin === e.id && <Check size={14} strokeWidth={2.2} style={{ color: 'var(--s-ink)', flex: 'none' }} />}
                                            </button>
                                        ))}

                                        <div className="s-fe__opt" style={{ cursor: 'default', opacity: .55 }}>
                                            <span style={{ flex: 1, minWidth: 0 }}>
                                                <span style={{ display: 'block', fontSize: 13, color: 'var(--s-ink)' }}>{t(ENJIN_BELUM.kunciLabel)}</span>
                                                <span className="s-dim">{t(ENJIN_BELUM.kunciNota)}</span>
                                            </span>
                                            <Badge tone="neutral"><Lock size={10} strokeWidth={2} />{t('formula.editor.s2.nanti')}</Badge>
                                        </div>
                                    </div>

                                    <Medan label={t('formula.editor.s2.setLabel')}>
                                        <Segmented
                                            value={String(bilSet)}
                                            onChange={(v) => setBilSet(Number(v))}
                                            options={[{ value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3', label: '3' }]}
                                        />
                                        <p className="s-fs__nota" style={{ margin: '8px 0 0' }}>
                                            {t('formula.editor.s2.setNota')}
                                        </p>
                                    </Medan>
                                </div>

                                {/* Kuning kekal DI SINI sahaja — ini amaran benar: bilangan
                                    babak menentukan nama medan, jadi menukarnya selepas 200
                                    produk berjalan akan merosakkan kad sedia ada. */}
                                <p className="s-fe__warn">
                                    {t('formula.editor.s2.amaran')}
                                </p>
                            </Soalan>
                        )}

                        {/* ---------- 3 · Master prompt ---------- */}
                        {langkah === 3 && (
                            <Soalan
                                tajuk={t('formula.editor.s3.tajuk')}
                                nota={t('formula.editor.s3.nota')}
                            >
                                <div style={{ display: 'grid', gap: 12 }}>

                                    {/* Suis mod. "Setiap set" ialah sebab tiga video daripada
                                        satu Generate boleh betul-betul berbeza format, bukan
                                        sekadar berbeza ayat. */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                        <Segmented
                                            value={promptMode}
                                            onChange={tukarMod}
                                            options={[
                                                { value: 'single', label: t('formula.editor.s3.modSatu') },
                                                { value: 'separate', label: t('formula.editor.s3.modSetiap') },
                                            ]}
                                        />
                                        <span className="s-fs__hint">
                                            {promptMode === 'single'
                                                ? t('formula.editor.s3.hintSatu', { bil: bilSet })
                                                : t('formula.editor.s3.hintSetiap', { bil: bilSet })}
                                        </span>
                                    </div>

                                    {/* Tab set — satu set pada satu masa. Titik kuning pada tab
                                        memberitahu set mana belum diisi tanpa perlu membukanya. */}
                                    {promptMode === 'separate' && (
                                        <div style={{
                                            display: 'flex', gap: 4,
                                            borderBottom: '1px solid var(--s-line)',
                                        }}>
                                            {senaraiSet.map((n) => {
                                                const kini = n === setAktif;
                                                const kosong = !String(promptSets[kunci(n)] || '').trim();
                                                return (
                                                    <button
                                                        key={n}
                                                        type="button"
                                                        onClick={() => setSetAktif(n)}
                                                        aria-current={kini ? 'true' : undefined}
                                                        style={{
                                                            padding: '6px 12px', border: 0, background: 'none',
                                                            fontFamily: 'inherit', fontSize: 13, cursor: 'pointer',
                                                            color: kini ? 'var(--s-ink)' : 'var(--s-ink-3)',
                                                            borderBottom: kini ? '1.5px solid var(--s-ink)' : '1.5px solid transparent',
                                                            marginBottom: -1,
                                                        }}
                                                    >
                                                        Set {n}
                                                        {kosong && (
                                                            <span style={{ color: 'var(--s-warn)', marginLeft: 6 }} title={t('formula.editor.s3.belumDiisi')}>•</span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {!teksAktif.trim() && (
                                        <div>
                                            <Btn
                                                variant="ghost"
                                                onClick={() => tukarTeks(RANGKA_CONTOH)}
                                                icon={<Sparkles size={14} strokeWidth={1.9} />}
                                            >
                                                {t('formula.editor.s3.mulaContoh')}
                                            </Btn>
                                            <p className="s-fs__nota" style={{ margin: '8px 0 0' }}>
                                                {t('formula.editor.s3.mulaContohNota')}
                                            </p>
                                        </div>
                                    )}

                                    <textarea
                                        className="s-fe__inp"
                                        style={{
                                            minHeight: 280, resize: 'vertical',
                                            fontFamily: 'var(--s-mono)', fontSize: 12, lineHeight: 1.65,
                                        }}
                                        value={teksAktif}
                                        onChange={(e) => tukarTeks(e.target.value)}
                                        placeholder={promptMode === 'single'
                                            ? t('formula.editor.s3.phSatu')
                                            : t('formula.editor.s3.phSetiap', { n: setAktif })}
                                        aria-label={promptMode === 'single' ? 'Master prompt' : `Master prompt set ${setAktif}`}
                                    />

                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                        <span className="s-fs__hint">{t('formula.editor.aksara', { n: teksAktif.length })}</span>
                                        {teksAktif.trim() && teksAktif.trim().length < 200 && (
                                            <span className="s-fs__hint">
                                                {t('formula.editor.s3.promptPendek')}
                                            </span>
                                        )}
                                        {promptMode === 'separate' && setKosong.length > 0 && (
                                            <span className="s-fs__hint" style={{ color: 'var(--s-warn)' }}>
                                                {t('formula.editor.s3.setBelumDiisi', { senarai: setKosong.join(', ') })}
                                            </span>
                                        )}
                                    </div>

                                    {/* Kontrak dipapar, bukan disorok: pelanggan yang tidak
                                        nampak kontrak akan menulisnya sendiri dalam promptnya,
                                        kemudian dua kontrak bertindih dan AI pilih salah satu. */}
                                    <div style={{ borderTop: '1px solid var(--s-line)', paddingTop: 12 }}>
                                        <button
                                            type="button"
                                            className="s-fe__more"
                                            aria-expanded={bukaKontrak}
                                            onClick={() => setBukaKontrak((v) => !v)}
                                        >
                                            <ChevronRight
                                                size={14}
                                                strokeWidth={2}
                                                style={{ transform: bukaKontrak ? 'rotate(90deg)' : 'none', transition: 'transform .15s ease' }}
                                            />
                                            <span style={{ flex: 1, textAlign: 'left' }}>{t('formula.editor.s3.kontrakTajuk')}</span>
                                            <Badge tone="neutral"><Lock size={10} strokeWidth={2} />{t('formula.editor.s3.kontrakBadge')}</Badge>
                                        </button>

                                        {bukaKontrak && (
                                            <div style={{ marginTop: 10, display: 'grid', gap: 9 }}>
                                                <p className="s-fs__nota" style={{ margin: 0 }}>
                                                    {t('formula.editor.s3.kontrakNota')}
                                                </p>
                                                {/* ⛔ ISI kotak di bawah (binaKontrak) SENGAJA tidak
                                                    diterjemah — ia nama medan JSON sebenar yang dicari
                                                    oleh robot Flow. Menterjemahnya mematikan automasi. */}
                                                <div className="s-fs__box">
                                                    <p className="s-fs__boxteks">{binaKontrak(enjin, bilSet, promptMode)}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Soalan>
                        )}

                        {/* ---------- 4 · Uji ---------- */}
                        {langkah === 4 && (
                            <Soalan
                                tajuk={t('formula.editor.s4.tajuk')}
                                nota={t('formula.editor.s4.nota')}
                            >
                                <div style={{ display: 'grid', gap: 16 }}>

                                    {ujian === 'belum' && (
                                        <>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                                <Btn onClick={jalankanUjian} icon={<Play size={14} strokeWidth={1.9} />}>
                                                    {t('formula.editor.s4.butangUji')}
                                                </Btn>
                                                <span className="s-fs__hint">
                                                    {t('formula.editor.s4.hintUji')}
                                                </span>
                                            </div>

                                            {/* PROTOTAIP: supaya keadaan GAGAL boleh dinilai tanpa
                                                menunggu AI sebenar mengecewakan kita. */}
                                            <label style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 8,
                                                fontSize: 12.5, color: 'var(--s-ink-3)', cursor: 'pointer',
                                            }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tunjukGagal}
                                                    onChange={(e) => setTunjukGagal(e.target.checked)}
                                                />
                                                {t('formula.editor.s4.suisGagal')}
                                            </label>
                                        </>
                                    )}

                                    {ujian === 'jalan' && (
                                        <Btn disabled icon={<Play size={14} strokeWidth={1.9} />}>{t('formula.editor.s4.sedangUji')}</Btn>
                                    )}

                                    {(ujian === 'lulus' || ujian === 'gagal') && (
                                        <>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                                                {ujian === 'lulus' ? (
                                                    <>
                                                        <Badge tone="ok"><Check size={10} strokeWidth={2.4} />{t('formula.editor.lulus')}</Badge>
                                                        {/* Dua ayat PENUH, bukan serpihan yang dicantum —
                                                            susunan perkataan English berbeza. */}
                                                        <span className="s-fs__hint">
                                                            {duaBabak
                                                                ? t('formula.editor.s4.lulusNota2')
                                                                : t('formula.editor.s4.lulusNota')}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Badge tone="stop"><X size={10} strokeWidth={2.4} />{t('formula.editor.s4.belumLulus')}</Badge>
                                                        <span className="s-fs__hint">{t('formula.editor.s4.gagalNota')}</span>
                                                    </>
                                                )}
                                            </div>

                                            {/* Jadual per set — pelanggan kena tahu set MANA yang
                                                gagal, bukan sekadar "ada yang gagal". */}
                                            <div style={{
                                                border: '1px solid var(--s-line)',
                                                borderRadius: 'var(--s-r-sm)', overflow: 'hidden',
                                            }}>
                                                {senaraiSet.map((n, i) => {
                                                    const gagal = ujian === 'gagal' && n === setGagal;
                                                    return (
                                                        <div
                                                            key={n}
                                                            style={{
                                                                display: 'flex', alignItems: 'center', gap: 10,
                                                                padding: '9px 11px',
                                                                borderBottom: i === senaraiSet.length - 1 ? 0 : '1px solid var(--s-line)',
                                                                background: gagal ? 'var(--s-stop-soft)' : 'transparent',
                                                            }}
                                                        >
                                                            <span style={{ width: 46, flex: 'none', fontSize: 13, color: 'var(--s-ink)' }}>Set {n}</span>
                                                            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: gagal ? 'var(--s-stop)' : 'var(--s-ink-3)' }}>
                                                                {gagal
                                                                    ? t('formula.editor.s4.barisGagal')
                                                                    : (duaBabak
                                                                        ? t('formula.editor.s4.barisOk2')
                                                                        : t('formula.editor.s4.barisOk'))}
                                                            </span>
                                                            {gagal ? (
                                                                <Btn size="sm" variant="ghost" onClick={() => betulkanSet(n)}>
                                                                    {t('formula.editor.s4.betulkanSet', { n })}
                                                                </Btn>
                                                            ) : (
                                                                <span style={{ fontSize: 12.5, color: 'var(--s-ok)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                                                    <Check size={12} strokeWidth={2.2} />{t('formula.editor.s4.lengkap')}
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {ujian === 'lulus' && (
                                                <div style={{ display: 'grid', gap: 9 }}>
                                                    <div className="s-fe__lbl" style={{ marginBottom: 0 }}>{t('formula.editor.s4.hasilSet1')}</div>
                                                    <KotakHasil label={t('formula.editor.s4.promptGambar')} teks={CONTOH_HASIL.gambar} />
                                                    <KotakHasil
                                                        label={duaBabak
                                                            ? t('formula.editor.s4.promptVideo1')
                                                            : t('formula.editor.s4.promptVideo')}
                                                        teks={CONTOH_HASIL.video}
                                                    />
                                                    {duaBabak && <KotakHasil label={t('formula.editor.s4.promptVideo2')} teks={CONTOH_HASIL.video2} />}
                                                    <KotakHasil label={t('formula.editor.s4.captionTiktok')} teks={CONTOH_HASIL.caption} />
                                                </div>
                                            )}

                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                                <Btn
                                                    variant="ghost"
                                                    onClick={() => setUjian('belum')}
                                                    icon={<Play size={14} strokeWidth={1.9} />}
                                                >
                                                    {t('formula.editor.s4.ujiSemula')}
                                                </Btn>
                                                <span className="s-fs__hint">{t('formula.editor.s4.kuotaNota')}</span>
                                            </div>

                                            {/* Prototaip WAJIB mengaku dirinya prototaip di skrin yang
                                                paling mudah disalah anggap sebagai hasil sebenar. */}
                                            <p className="s-fe__warn" style={{ margin: 0 }}>
                                                {t('formula.editor.s4.amaranProto')}
                                            </p>
                                        </>
                                    )}
                                </div>
                            </Soalan>
                        )}

                        {/* ---------- 5 · Simpan ---------- */}
                        {langkah === AKHIR && (
                            <Soalan
                                tajuk={t('formula.editor.s5.tajuk')}
                                nota={t('formula.editor.s5.nota')}
                            >
                                <div style={{ display: 'grid', gap: 18 }}>
                                    <div>
                                        <BarisRingkasan label={t('formula.editor.s5.lblNama')}>{nama.trim() || '—'}</BarisRingkasan>
                                        <BarisRingkasan label={t('formula.editor.s5.lblBentuk')}>
                                            {enjin ? t('formula.editor.s5.nilaiBabak', { n: enjin }) : '—'}
                                        </BarisRingkasan>
                                        <BarisRingkasan label={t('formula.editor.s5.lblSet')}>{bilSet}</BarisRingkasan>
                                        <BarisRingkasan label={t('formula.editor.s5.lblMod')}>
                                            {promptMode === 'single'
                                                ? t('formula.editor.s5.modSatu')
                                                : t('formula.editor.s5.modSetiap', { bil: bilSet })}
                                        </BarisRingkasan>
                                        <BarisRingkasan label={t('formula.editor.s5.lblPanjang')}>
                                            {t('formula.editor.aksara', { n: jumlahAksara })}
                                        </BarisRingkasan>
                                        <BarisRingkasan label={t('formula.editor.s5.lblUjian')}>
                                            <Badge tone="ok"><Check size={10} strokeWidth={2.4} />{t('formula.editor.lulus')}</Badge>
                                        </BarisRingkasan>
                                    </div>

                                    <div>
                                        <div className="s-fe__lbl" style={{ marginBottom: 8 }}>{t('formula.editor.s5.rupaRak')}</div>
                                        <Card style={{ maxWidth: 300, overflow: 'hidden' }}>
                                            <div style={{
                                                height: 150, background: 'var(--s-ground)',
                                                borderBottom: '1px solid var(--s-line)', display: 'grid', placeItems: 'center',
                                            }}>
                                                <div style={{
                                                    width: 74, height: 132, borderRadius: 9,
                                                    border: '1px solid var(--s-line-strong)', background: 'var(--s-fill)',
                                                    display: 'grid', placeItems: 'center',
                                                }}>
                                                    <Play size={17} strokeWidth={1.6} style={{ color: 'var(--s-ink-3)' }} />
                                                </div>
                                            </div>
                                            <CardBody>
                                                <h3 className="s-cardname" style={{ marginBottom: 5 }}>
                                                    {nama.trim() || t('formula.editor.tajuk.tanpaNama')}
                                                </h3>
                                                <p className="s-meta" style={{ fontSize: 13, marginBottom: 9 }}>
                                                    {huraian.trim() || t('formula.editor.s5.huraianKosong')}
                                                </p>
                                                <span className="s-dim">{t('formula.editor.s5.kadMeta', { babak: enjin, set: bilSet })}</span>
                                            </CardBody>
                                        </Card>
                                    </div>
                                </div>
                            </Soalan>
                        )}

                    </div>
                </div>
            </div>

            {/* ---------- Bar kaki halaman ---------- */}
            <div className="s-fs__foot">
                <Btn
                    variant="ghost"
                    onClick={kebelakang}
                    disabled={langkah === 1}
                    icon={<ArrowLeft size={14} strokeWidth={1.9} />}
                >
                    {t('formula.editor.kaki.kembali')}
                </Btn>

                <span
                    className="s-fs__hint"
                    style={{
                        flex: 1, minWidth: 0, textAlign: 'center',
                        color: petunjukSekat && langkah === 3 && promptMode === 'separate' && setKosong.length
                            ? 'var(--s-warn)' : undefined,
                    }}
                >
                    {petunjukSekat || t('formula.editor.kaki.langkah', { kini: langkah, jumlah: AKHIR })}
                </span>

                {/* Simpan draf — senyap, sebelah kiri tindakan utama.
                    ⛔ DISOROK pada langkah terakhir: di situ butang utama
                       memang "Simpan formula", dan dua butang bertulis
                       "Simpan" bersebelahan memaksa pengguna berhenti
                       membaca untuk meneka yang mana satu betul. */}
                {langkah !== AKHIR && (
                    <Btn variant="ghost" disabled={!nama.trim()}>
                        {t('formula.editor.kaki.simpanDraf')}
                    </Btn>
                )}

                <Btn
                    onClick={kehadapan}
                    disabled={!bolehTeruskan}
                    icon={langkah === AKHIR
                        ? <Check size={14} strokeWidth={2} />
                        : <ArrowRight size={14} strokeWidth={1.9} />}
                >
                    {langkah === AKHIR
                        ? t('formula.editor.kaki.simpanFormula')
                        : t('formula.editor.kaki.seterusnya')}
                </Btn>
            </div>

        </div>
    );
}
